import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BalanceCard from "@/components/billing/BalanceCard";
import TierCard from "@/components/billing/TierCard";
import PackageCard from "@/components/billing/PackageCard";
import HistoryList from "@/components/billing/HistoryList";
import CheckoutModal from "@/components/billing/CheckoutModal";
import TransferModal from "@/components/billing/TransferModal";

function formatPeriodEnd(iso, locale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

export default function BillingPage() {
  const { t, i18n } = useTranslation();
  const { user, refreshMe } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeEnabled, setStripeEnabled] = useState(false);

  const [checkout, setCheckout] = useState(null); // { mode, tier?, pkg? }
  const [transfer, setTransfer] = useState(false);
  const [toast, setToast] = useState(null); // { type, text }
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTier = user?.subscription?.tier || "free";
  const balance = user?.nexa_tokens?.balance ?? 0;
  const unlimited = !!user?.nexa_tokens?.unlimited;
  const tierInfo = tiers.find((t) => t.id === currentTier);

  const showToast = useCallback((text, type = "success") => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, pkgsRes, histRes, configRes] = await Promise.all([
        api.get("/billing/tiers"),
        api.get("/billing/packages"),
        api.get("/billing/history"),
        api.get("/billing/config"),
      ]);
      setTiers(tiersRes.data.tiers);
      setPackages(pkgsRes.data.packages);
      setHistory(histRes.data.transactions);
      setStripeEnabled(!!configRes.data?.stripe_enabled);
    } catch (e) {
      console.warn("billing load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Phase 11: handle post-Stripe-redirect query params. We show a toast +
  // trigger a refetch of /me so the newly-activated tier lands in the UI
  // once the webhook has completed. The URL is cleaned up either way so
  // a page refresh doesn't re-trigger the toast.
  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    if (!success && !canceled) return;

    if (success === "true") {
      showToast(t("billing.checkout.stripe_success_processing"), "success");
      // Poll a couple of times — the webhook usually lands within 1-3s.
      const kick = async () => {
        await refreshMe?.();
        await loadAll();
      };
      kick();
      const t1 = setTimeout(kick, 2000);
      const t2 = setTimeout(kick, 5000);
      // Clear query params without pushing history.
      setSearchParams({}, { replace: true });
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (canceled === "true") {
      showToast(t("billing.checkout.stripe_canceled"), "warn");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCheckoutSuccess = useCallback(
    async (payload) => {
      setCheckout(null);
      if (payload?.subscription) {
        showToast(t("billing.checkout.success_subscribe"));
      } else if (payload?.package) {
        showToast(
          t("billing.checkout.success_recharge", {
            tokens: payload.package.tokens,
          }),
        );
      }
      await refreshMe?.();
      await loadAll();
    },
    [refreshMe, loadAll, showToast, t],
  );

  const handleTransferSuccess = useCallback(
    async ({ amount }) => {
      setTransfer(false);
      showToast(t("billing.transfer.success", { amount }));
      await refreshMe?.();
      await loadAll();
    },
    [refreshMe, loadAll, showToast, t],
  );

  return (
    <div className="nx-page-bg" data-testid="billing-page">
      <div className="px-5 pt-7 pb-6 flex items-center justify-between">
        <div>
          <Link
            to="/logros"
            data-testid="billing-back"
            className="inline-flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:text-[#E5E7EB]"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            {t("common.back")}
          </Link>
          <h1
            className="mt-1 text-[22px] sm:text-[24px] font-semibold text-[#E5E7EB] leading-tight"
            data-testid="billing-page-title"
          >
            {t("billing.page.title")}
          </h1>
          <div className="mt-0.5 flex flex-col sm:flex-row sm:items-center sm:gap-2.5">
            <p className="text-[12px] text-[#9CA3AF]">
              {t("billing.page.subtitle")}
            </p>
            {stripeEnabled ? (
              <span
                data-testid="billing-stripe-badge"
                className="mt-1.5 sm:mt-0 self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-medium uppercase tracking-[0.14em] bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.25)] text-[#00E5FF] backdrop-blur-sm"
                title={t("billing.checkout.stripe_connected_hint")}
              >
                <ShieldCheck className="h-3 w-3" strokeWidth={2.25} />
                {t("billing.checkout.stripe_connected_title")}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-5 pb-24 space-y-6">
        <BalanceCard
          balance={balance}
          unlimited={unlimited}
          tier={tierInfo}
          periodEnd={formatPeriodEnd(user?.subscription?.current_period_end, i18n.language)}
          onRecharge={() => setCheckout({ mode: "recharge", pkg: packages[1] || packages[0] })}
          onTransfer={() => setTransfer(true)}
        />

        {/* Tiers */}
        <section data-testid="billing-tiers-section">
          <h2 className="text-[12.5px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-3">
            {t("billing.tiers.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(loading ? [] : tiers).map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                currentTier={currentTier}
                onPick={(picked) => setCheckout({ mode: "subscribe", tier: picked })}
              />
            ))}
          </div>
        </section>

        {/* Packages */}
        <section data-testid="billing-packages-section">
          <h2 className="text-[12.5px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-3">
            {t("billing.packages.title")}
          </h2>
          <div className="grid grid-cols-3 gap-2.5">
            {(loading ? [] : packages).map((p) => (
              <PackageCard
                key={p.id}
                pkg={p}
                onBuy={(picked) => setCheckout({ mode: "recharge", pkg: picked })}
              />
            ))}
          </div>
        </section>

        {/* History */}
        <section data-testid="billing-history-section">
          <h2 className="text-[12.5px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-1">
            {t("billing.history.title")}
          </h2>
          <div className="nx-glass rounded-2xl p-2.5">
            <HistoryList transactions={history} />
          </div>
        </section>
      </div>

      <CheckoutModal
        open={!!checkout}
        mode={checkout?.mode}
        tier={checkout?.tier}
        pkg={checkout?.pkg}
        stripeEnabled={stripeEnabled}
        onClose={() => setCheckout(null)}
        onSuccess={handleCheckoutSuccess}
      />
      <TransferModal
        open={transfer}
        onClose={() => setTransfer(false)}
        onSuccess={handleTransferSuccess}
        currentBalance={balance}
        unlimited={unlimited}
      />

      {toast ? (
        <div
          data-testid="billing-toast"
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10200] px-4 py-2.5 rounded-full text-[12.5px] bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] backdrop-blur-md animate-fade-in-up shadow-[0_0_22px_rgba(0,229,255,0.35)]"
        >
          {toast.text}
        </div>
      ) : null}
    </div>
  );
}
